import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useAdminNominations, useReviewNomination } from '../../hooks/useQuestAdmin';
import { downloadCsv, fmtDate } from './questShared';
import {
  BTN, BTN_DANGER, BTN_SECONDARY, Card, ErrorNote, INPUT, Loading,
} from './adminUi';

// Values in Action nominations (brief: Manage submissions — approve, reject,
// request clarification). Approving pays the colleague the Quest's points.
// Rejecting or asking for detail needs a note: the person who nominated sees it.

const FILTERS = [
  ['pending', 'Waiting'],
  ['clarify', 'Asked for detail'],
  ['approved', 'Approved'],
  ['rejected', 'Not approved'],
  ['', 'All'],
];

function Nomination({ n }) {
  const review = useReviewNomination();
  const [note, setNote] = useState('');
  const decide = (decision) => review.mutate({ id: n.id, decision, note });
  const open = n.status === 'pending' || n.status === 'clarify';

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-base font-semibold text-gray-900">{n.nominee_name || 'Unknown'}</span>
        <span className="text-sm font-semibold text-brand">{n.value_title}</span>
        <span className="ml-auto text-xs text-gray-400">{fmtDate(n.created_at)}</span>
      </div>
      <p className="mt-0.5 text-xs text-gray-500">Nominated by {n.nominator_name || 'Unknown'}</p>
      <p className="mt-2 whitespace-pre-line text-sm text-gray-800">“{n.story}”</p>

      {n.review_note ? (
        <p className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span className="font-semibold">{n.reviewed_by_name || 'Comms'}:</span> {n.review_note}
        </p>
      ) : null}

      {n.status === 'approved' ? (
        <p className="mt-2 text-sm text-emerald-700">
          Approved by {n.reviewed_by_name || 'Comms'} on {fmtDate(n.reviewed_at)} · +{n.points_awarded} points
        </p>
      ) : null}
      {n.status === 'rejected' ? (
        <p className="mt-2 text-sm text-gray-500">Not approved · {fmtDate(n.reviewed_at)}</p>
      ) : null}
      {n.status === 'clarify' ? (
        <p className="mt-2 text-sm text-sky-700">Waiting for {n.nominator_name || 'them'} to add detail.</p>
      ) : null}

      {open ? (
        <div className="mt-3 space-y-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} className={INPUT}
            placeholder="Note to the person who nominated (needed to ask for detail or to reject)" />
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={review.isPending} onClick={() => decide('approve')} className={BTN}>
              Approve
            </button>
            <button type="button" disabled={review.isPending} onClick={() => decide('clarify')} className={BTN_SECONDARY}>
              Ask for detail
            </button>
            <button type="button" disabled={review.isPending} onClick={() => decide('reject')} className={BTN_DANGER}>
              Not approved
            </button>
          </div>
          {review.isError ? <ErrorNote error={review.error} /> : null}
        </div>
      ) : null}
    </Card>
  );
}

function NominationsTab() {
  const [status, setStatus] = useState('pending');
  const q = useAdminNominations(status);

  // the brief's "export: Nominations" — whatever the filter is showing
  const exportCsv = () => downloadCsv(`nano-quest-nominations-${status || 'all'}.csv`, [
    ['Date', (n) => fmtDate(n.created_at)],
    ['Quarter', (n) => n.quarter],
    ['Nominee', (n) => n.nominee_name],
    ['Value', (n) => n.value_title],
    ['Nominated by', (n) => n.nominator_name],
    ['What they did', (n) => n.story],
    ['Status', (n) => FILTERS.find(([k]) => k === n.status)?.[1] || n.status],
    ['Comms note', (n) => n.review_note],
    ['Reviewed by', (n) => n.reviewed_by_name],
    ['Reviewed on', (n) => fmtDate(n.reviewed_at)],
    ['Points', (n) => n.points_awarded],
  ], q.data || []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1">
        {FILTERS.map(([k, label]) => (
          <button key={k || 'all'} type="button" onClick={() => setStatus(k)}
            className={`rounded-full px-3 py-1 text-sm font-semibold ${
              status === k ? 'bg-brand text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            {label}
          </button>
        ))}
        <button type="button" onClick={exportCsv} disabled={!q.data?.length} className={`${BTN_SECONDARY} ml-auto`}>
          <ArrowDownTrayIcon className="h-4 w-4" /> CSV
        </button>
      </div>
      <Loading q={q} what="the nominations" />
      {q.data && !q.data.length ? (
        <Card className="px-4 py-6 text-center text-sm text-gray-500">Nothing here.</Card>
      ) : null}
      {(q.data || []).map((n) => <Nomination key={n.id} n={n} />)}
    </div>
  );
}

export default NominationsTab;
